/**
 * useExpenseModals Hook
 * Manages modal state for Expense component using consolidated state
 */

import { useState, useCallback } from "react";

// Initial modal state
const initialModalState = {
  create: false,
  edit: false,
  delete: false,
};

export const useExpenseModals = () => {
  // Consolidated modal state
  const [modals, setModals] = useState(initialModalState);
  
  // Individual modal states (for backward compatibility)
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  
  // Sync individual states with consolidated state
  const updateModals = useCallback((updates) => {
    setModals((prev) => {
      const newState = { ...prev, ...updates };
      
      // Sync individual states
      setIsCreateModalOpen(newState.create);
      setIsEditModalOpen(newState.edit);
      setIsDeleteModalOpen(newState.delete);
      
      return newState;
    });
  }, []);

  // Consolidated modal handlers
  const openCreateModal = useCallback(() => {
    updateModals({ create: true });
  }, [updateModals]);

  const closeCreateModal = useCallback(() => {
    updateModals({ create: false });
  }, [updateModals]);

  const openEditModal = useCallback(() => {
    updateModals({ edit: true });
  }, [updateModals]);

  const closeEditModal = useCallback(() => {
    updateModals({ edit: false });
  }, [updateModals]);

  const openDeleteModal = useCallback(() => {
    updateModals({ delete: true });
  }, [updateModals]);

  const closeDeleteModal = useCallback(() => {
    updateModals({ delete: false });
  }, [updateModals]);

  const closeAllModals = useCallback(() => {
    setModals(initialModalState);
    setIsCreateModalOpen(false);
    setIsEditModalOpen(false);
    setIsDeleteModalOpen(false);
  }, []);

  return {
    // Consolidated modal state
    modals,
    
    // Individual modal states (for backward compatibility)
    isCreateModalOpen,
    isEditModalOpen,
    isDeleteModalOpen,
    
    // Open handlers
    openCreateModal,
    openEditModal,
    openDeleteModal,
    
    // Close handlers
    closeCreateModal,
    closeEditModal,
    closeDeleteModal,
    closeAllModals,
    
    // Direct setters (for backward compatibility)
    setIsCreateModalOpen,
    setIsEditModalOpen,
    setIsDeleteModalOpen,
    
    // Consolidated state setter
    setModals,
  };
};
